import { DRUID_PATH } from '@somnhold/content';
import { idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import { cellAtWorld, cellId, heroWalkable, touchGrid } from '../grid.ts';
import type { BattleState, Enemy, Hero, SendaNpc, WinterRules, WinterSerpents, WinterState } from '../types.ts';
import { damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { stepToward } from './enemies.ts';
import { sapFlow, wayAlong, wayAt, type BoughsLayout, type HollowLayout, type SapLayout } from './druid-winter-layout.ts';

/**
 * The Grandsire's crown over the clouds (docs/sendas/druida.md, chapter 4; the grounds in battle/druid-winter-layout.ts).
 *
 * - The boughs (act 1, `boughs`): up from bough to bough to the top of the crown. Snow comes off the boughs above in
 *   blocks, one where the hero stands and one where it is going, the bough creaking 3 s before; each leaves a heap that
 *   slows. Soot crows come down in flocks.
 * - The wood that opens (act 2, `hollow`): Ceda bandages Hob's leg in a hollow while shell serpents travel inside the
 *   wood (out of reach: `winterHidden`, read by combat's `isRevealed`) and come out next to whoever stands still (Hob
 *   and Ceda always; the hero once still a moment: «quieto es donde salen»), the bark swelling 3 s before. A hero on the
 *   swelling as it bursts treads it down (it takes a little, nobody else is bitten, the serpent comes up stunned).
 * - The sap of the crown (act 3, the test, `sap`): a pulse of sap up the trunk every 3 s, split even at each fork among
 *   the branches whose knot is open; a bud that gets what it asks opens, more and it cracks (shut 8 s). Three open at
 *   once and the crown runs: the low crown first (the document's example), then the high one. The hero turns a knot
 *   standing still by it (its Raíz), and the frozen wood bites back. The frost that climbs comes up the trunk to a closed
 *   knot and opens it (3 s of frost on it, unless struck off), and claws at the hero; the serpents come out by whoever
 *   stands still, Hob in his hollow too.
 *
 * Cost: a handful of points a tick (the blocks, the heaps, the serpents, the knots); the sap's split once a pulse over a
 * crown of a dozen nodes (docs/RENDIMIENTO.md, «Reglas para lo nuevo»).
 */

type St = WinterState;
type Pt = { x: number; y: number };
type Sap = NonNullable<WinterRules['sap']>;

const fresh = (): St => ({
  done: -1,
  lost: 0,
  t: 0,
  job: -1,
  held: 0,
  blocks: [],
  heaps: [],
  snowNext: 0,
  crowNext: 0,
  falls: 0,
  struck: 0,
  bridged: [],
  flocks: 0,
  near: 0,
  sp: {},
  serpN: 0,
  serpNext: 0,
  swells: 0,
  treads: 0,
  bites: 0,
  work: 0,
  flinch: 0,
  stage: 0,
  closed: [],
  buds: [],
  crackUntil: [],
  pulseNext: 0,
  pulses: 0,
  turns: 0,
  cracks: 0,
  ran: -1,
  streak: 0,
  boss: 0,
  way: [],
  bknot: -1,
  bwarn: -1,
  bhp: 0,
  brest: 0,
  claw: -1,
  cx: 0,
  cy: 0,
  clawNext: 0,
  claws: 0,
  opened: 0,
  pend: [],
});

/* ---------- rules from the layouts (the fight's builder in campaign/sendas.ts and the drawing build them the same) ---------- */

const serpOf = (P: { serp: WinterSerpents & { d: number } }): WinterSerpents => {
  const { d: _d, ...rest } = P.serp;
  void _d;
  return { ...rest };
};

export const boughsRules = (L: BoughsLayout): WinterRules => {
  const P = DRUID_PATH.boughs;
  return {
    kind: 'boughs',
    bossHp: 0,
    serpHp: 0,
    still: 0,
    boughs: {
      way: L.way.map((p) => ({ ...p })),
      gaps: L.gaps.map((g) => ({ a: { ...g.a }, b: { ...g.b }, cells: g.cells.map((h) => ({ q: h.q, r: h.r })) })),
      sway: { ...P.sway },
      top: { ...L.top },
      rim: L.rim.map((p) => ({ ...p })),
      reach: P.reach,
      snow: { ...P.snow },
      crows: { ...P.crows },
    },
  };
};

export const hollowRules = (L: HollowLayout, serpHp: number): WinterRules => {
  const P = DRUID_PATH.hollow;
  void L;
  return { kind: 'hollow', bossHp: 0, serpHp, still: P.still, serp: serpOf(P), hollow: { need: P.need, flinch: P.flinch } };
};

export const sapRules = (L: SapLayout, bossHp: number, serpHp: number): WinterRules => {
  const P = DRUID_PATH.sap;
  return {
    kind: 'sap',
    bossHp,
    serpHp,
    still: P.still,
    serp: serpOf(P),
    sap: {
      nodes: L.nodes.map((n) => ({ x: n.x, y: n.y, parent: n.parent, kids: [...n.kids], need: n.need, knot: n.knot, stage: n.stage })),
      roots: [...L.roots],
      knots: L.knots.map((k) => ({ ...k })),
      foot: { ...L.foot },
      plug: { ...L.plug },
      pulse: P.pulse,
      crack: P.crack,
      need: P.need,
      hold: P.hold,
      cut: P.cut,
      reach: P.reach,
      chillBp: P.chillBp,
      boss: { first: P.boss.first, pace: P.boss.pace, warn: P.boss.warn, scareBp: P.boss.scareBp, rest: P.boss.rest, claw: { ...P.boss.claw } },
    },
  };
};

/* ---------- helpers ---------- */

const heroOf = (s: BattleState): Hero | undefined => s.heroes.find((x) => x.downTicks === 0);
const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);
const WHOM = ['', 'hob', 'ceda'];

/** A nightmare of the act at a point, its life as given (or the kind's, scaled as the fight's are). */
const spawn = (s: BattleState, kind: 'crow' | 'shellSerpent' | 'rime', x: number, y: number, hp = 0): Enemy => {
  const e = spawnEnemy(s, kind, 1, false, 0, x, y, hp);
  if (!hp && s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  return e;
};

/** A bite on a person (not a nightmare's blow standing at them): its blows off their life. */
const hurtNpc = (s: BattleState, n: SendaNpc | undefined, blows: number): void => {
  if (!n || n.hp <= 0 || blows <= 0) return;
  n.hp -= blows;
  n.hit = true;
  ev(s, { t: 'senda', kind: 'hit', npc: n.id, x: n.x, y: n.y });
  if (n.hp <= 0) {
    n.hp = 0;
    ev(s, { t: 'senda', kind: 'down', npc: n.id, x: n.x, y: n.y });
  }
};

/** Whether a hero counts as still for the serpents (and for its Raíz at a knot). */
const still = (h: Hero, ticks: number): boolean => !h.moving && (h.still ?? 0) >= ticks;

/** A point on the wood next to another (a sixth of a turn picked by the seed's draw), else the point itself. */
const beside = (s: BattleState, x: number, y: number, d: number): Pt => {
  const DX = [1000, 500, -500, -1000, -500, 500];
  const DY = [0, -866, -866, 0, 866, 866];
  const k0 = rngInt(s.rng, 6);
  for (let i = 0; i < 6; i++) {
    const k = (k0 + i) % 6;
    const tx = x + idiv((DX[k] as number) * d, 1000);
    const ty = y + idiv((DY[k] as number) * d, 1000);
    if (heroWalkable(s, cellAtWorld(s, tx, ty))) return { x: tx, y: ty };
  }
  return { x, y };
};

/* ---------- act 1: the boughs ---------- */

/** Whether the wind has gap `i` swung together now (each `lag` after the one below, `open` of every `every`). */
export const gapOpen = (r: NonNullable<WinterRules['boughs']>, i: number, t: number): boolean => gapOpenAt(r.sway, i, t);

/** The same from the wind alone (the drawing reads it between ticks: `t` may carry a fraction). */
export const gapOpenAt = (sw: { every: number; open: number; lag: number }, i: number, t: number): boolean => {
  const k = (((t - ((i * sw.lag) % sw.every)) % sw.every) + sw.every) % sw.every;
  return k < sw.open;
};

/** The gaps apart as the grid's plugs (sorted cell ids); who stands in one as it swings apart steps back onto a bough. */
const sway = (s: BattleState, r: NonNullable<WinterRules['boughs']>, w: St, cue: (c: string) => void): void => {
  let changed = !w.bridged.length;
  if (!w.bridged.length) w.bridged = r.gaps.map(() => -1);
  for (let i = 0; i < r.gaps.length; i++) {
    const now = gapOpen(r, i, s.waveT) ? 1 : 0;
    if (now === w.bridged[i]) continue;
    w.bridged[i] = now;
    changed = true;
    const g = r.gaps[i] as { a: Pt; b: Pt };
    ev(s, { t: 'senda', kind: now ? 'bridge' : 'apart', npc: '', x: idiv(g.a.x + g.b.x, 2), y: idiv(g.a.y + g.b.y, 2), n: i });
    if (now && i === 0) cue('bridge1');
  }
  if (!changed) return;
  const R = s.map.radius;
  const ids: number[] = [];
  r.gaps.forEach((g, i) => {
    if (w.bridged[i]) return;
    for (const c of g.cells) {
      const id = cellId(R, c.q, c.r);
      if (id >= 0) ids.push(id);
    }
  });
  ids.sort((a, b) => a - b);
  s.plugs = ids;
  touchGrid(s);
  // Nobody left over the cloud: onto the nearer end.
  const shut = new Set(ids);
  for (const h of s.heroes) {
    const at = cellAtWorld(s, h.x, h.y);
    if (!shut.has(at)) continue;
    const g = r.gaps.find((x) => x.cells.some((c) => cellId(R, c.q, c.r) === at));
    if (!g) continue;
    const da = (h.x - g.a.x) * (h.x - g.a.x) + (h.y - g.a.y) * (h.y - g.a.y);
    const db = (h.x - g.b.x) * (h.x - g.b.x) + (h.y - g.b.y) * (h.y - g.b.y);
    const p = da <= db ? g.a : g.b;
    h.x = h.ax = h.destX = p.x;
    h.y = h.ay = h.destY = p.y;
    h.path = [];
    h.moving = false;
  }
};

const boughsTick = (s: BattleState, r: NonNullable<WinterRules['boughs']>, w: St, cue: (c: string) => void): void => {
  const h = heroOf(s);
  if (w.done >= 0) return;
  sway(s, r, w, cue);
  const S = r.snow;
  // The blocks: one where the hero stands, the next where it is going; the bough creaks over each first.
  if (h && s.waveT >= S.first && s.tick >= w.snowNext) {
    w.snowNext = s.tick + S.every;
    // The second where it will be as the first lands, if it keeps going along the bough (up or down it); standing, a
    // little up the way.
    const here = wayAlong(r.way, h.x, h.y);
    const dest = h.moving ? wayAlong(r.way, h.destX, h.destY) : here + 1;
    const run = idiv(h.stats.speed * S.warn, 12);
    const at = wayAt(r.way, imax(0, dest >= here ? here + (h.moving ? run : idiv(S.lead, 2)) : here - run));
    const spots = [{ x: h.x, y: h.y }, at];
    for (let i = 0; i < S.n && i < spots.length; i++) {
      const q = spots[i] as Pt;
      w.blocks.push([q.x, q.y, s.tick + S.warn]);
      w.falls++;
      ev(s, { t: 'senda', kind: 'creak', npc: '', x: q.x, y: q.y, n: S.warn });
    }
    cue(`creak${w.falls >= S.n ? idiv(w.falls, S.n) : 1}`);
  }
  // A block landing: the blow round it, the heap it leaves.
  for (let i = w.blocks.length - 1; i >= 0; i--) {
    const b = w.blocks[i] as number[];
    if (s.tick < (b[2] as number)) continue;
    w.blocks.splice(i, 1);
    ev(s, { t: 'senda', kind: 'snowfall', npc: '', x: b[0] as number, y: b[1] as number });
    for (const x of s.heroes) {
      if (x.downTicks > 0 || !withinRange(x.x, x.y, b[0] as number, b[1] as number, S.r)) continue;
      damageHero(s, x, mulBp(x.stats.maxHp, S.bp), null, 'physical', true);
      w.struck++;
      cue(`struck${w.struck}`);
    }
    w.heaps.push([b[0] as number, b[1] as number, s.tick + S.heap]);
    if (w.heaps.length === 2) cue('heap2');
  }
  for (let i = w.heaps.length - 1; i >= 0; i--) if (s.tick >= ((w.heaps[i] as number[])[2] as number)) w.heaps.splice(i, 1);
  // The crows: a flock off the crown's edge, the side the hero is on.
  if (h && s.waveT >= r.crows.first && s.tick >= w.crowNext) {
    w.crowNext = s.tick + r.crows.every;
    const side = h.x >= 0 ? 1 : 0;
    const p = r.rim[(w.flocks * 2 + side) % r.rim.length] as Pt;
    w.flocks++;
    for (let k = 0; k < r.crows.n; k++) spawn(s, 'crow', p.x + (k % 3) * 300 - 300, p.y + idiv(k, 3) * 300);
    ev(s, { t: 'senda', kind: 'flock', npc: '', x: p.x, y: p.y, n: r.crows.n });
    cue(`flock${w.flocks}`);
  }
  // Near the top: Ceda's line; at it, won.
  if (h && !w.near && withinRange(h.x, h.y, r.top.x, r.top.y, r.reach * 3)) {
    w.near = 1;
    cue('near');
  }
  if (h && withinRange(h.x, h.y, r.top.x, r.top.y, r.reach)) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'top', npc: '', x: r.top.x, y: r.top.y });
    cue('top');
  }
};

/* ---------- the serpents (acts 2 and 3) ---------- */

const serpTick = (s: BattleState, r: WinterRules, w: St, cue: (c: string) => void): void => {
  const S = r.serp;
  if (!S || w.done >= 0) return;
  const h = heroOf(s);
  let alive = 0;
  for (const id of Object.keys(w.sp)) {
    const e = findEnemy(s, Number(id));
    if (!e || e.hp <= 0) delete w.sp[Number(id)];
    else alive++;
  }
  if (s.waveT >= S.first && s.tick >= w.serpNext && alive < S.max) {
    w.serpNext = s.tick + S.every;
    const e = spawn(s, 'shellSerpent', 0, 0, r.serpHp);
    w.sp[e.id] = [0, s.tick + idiv(S.travel, 2) + rngInt(s.rng, S.spread + 1), 0, 0, 0, 0];
    w.serpN++;
  }
  const ids = Object.keys(w.sp)
    .map(Number)
    .sort((a, b) => a - b);
  for (const id of ids) {
    const m = w.sp[id] as number[];
    const e = findEnemy(s, id);
    if (!e) continue;
    const mode = m[0] as number;
    if (s.tick < (m[1] as number)) continue;
    if (mode === 0) {
      // Next to whoever stands still: the hero once still a moment, else Hob or Ceda (who never move).
      let whom = -1;
      let at: Pt | null = null;
      if (h && still(h, r.still)) {
        whom = 0;
        at = { x: h.x, y: h.y };
      } else if (w.swells % S.npcEvery === 0) {
        const people = [1, 2].filter((k) => {
          const n = npcOf(s, WHOM[k] as string);
          const def = s.rules?.senda?.npcs.find((x) => x.id === WHOM[k]);
          return !!n && n.hp > 0 && !def?.safe;
        });
        if (people.length) {
          whom = people[rngInt(s.rng, people.length)] as number;
          const n = npcOf(s, WHOM[whom] as string) as SendaNpc;
          at = { x: n.x, y: n.y };
        }
      }
      if (whom < 0 || !at) {
        m[1] = s.tick + 12;
        continue;
      }
      const p = beside(s, at.x, at.y, 760);
      e.x = p.x;
      e.y = p.y;
      m[0] = 1;
      m[1] = s.tick + S.warn;
      m[2] = p.x;
      m[3] = p.y;
      m[4] = whom;
      m[5] = 0;
      w.swells++;
      ev(s, { t: 'senda', kind: 'swell', npc: WHOM[whom] || 'hero', x: p.x, y: p.y, n: S.warn });
      cue(`swell${w.swells}`);
      if (whom === 0) cue(`still${w.swells}`);
      continue;
    }
    if (mode === 1) {
      // It bursts: trodden by a hero standing on it, or it bites round it.
      const x = m[2] as number;
      const y = m[3] as number;
      const foot = s.heroes.find((o) => o.downTicks === 0 && withinRange(o.x, o.y, x, y, S.stomp));
      m[0] = 2;
      m[1] = s.tick + S.up;
      if (foot) {
        damageHero(s, foot, mulBp(foot.stats.maxHp, S.stompBp), e, 'physical', true);
        e.stun = imax(e.stun, S.stun);
        m[5] = 1;
        w.treads++;
        ev(s, { t: 'senda', kind: 'tread', npc: '', x, y });
        cue(`tread${w.treads}`);
        continue;
      }
      ev(s, { t: 'senda', kind: 'burst', npc: WHOM[m[4] as number] || 'hero', x, y });
      for (const o of s.heroes) {
        if (o.downTicks > 0 || !withinRange(o.x, o.y, x, y, S.r)) continue;
        damageHero(s, o, mulBp(o.stats.maxHp, S.bp), e, 'physical', true);
        w.bites++;
        cue(`bite${w.bites}`);
      }
      // (It goes for whom it came out by: a person bitten only if it came for them.)
      for (const n of s.senda?.npcs ?? []) {
        const def = s.rules?.senda?.npcs.find((d) => d.id === n.id);
        if (def?.safe || n.hp <= 0 || n.id !== WHOM[m[4] as number] || !withinRange(n.x, n.y, x, y, S.r)) continue;
        hurtNpc(s, n, S.npc);
        if (n.id === 'ceda' && r.hollow) w.flinch = s.tick + r.hollow.flinch;
      }
      continue;
    }
    // Out long enough: under the wood again.
    m[0] = 0;
    m[1] = s.tick + S.travel + rngInt(s.rng, S.spread + 1);
    ev(s, { t: 'senda', kind: 'sink', npc: '', x: e.x, y: e.y });
  }
};

/* ---------- act 2: the hollow ---------- */

const hollowTick = (s: BattleState, r: NonNullable<WinterRules['hollow']>, w: St, cue: (c: string) => void): void => {
  if (w.done >= 0) return;
  const ceda = npcOf(s, 'ceda');
  // Ceda's bandage: on while she isn't flinching from a bite.
  if (ceda && ceda.hp > 0 && s.tick >= w.flinch) w.work++;
  if (w.work >= r.need) {
    w.done = s.tick;
    const hob = npcOf(s, 'hob');
    ev(s, { t: 'senda', kind: 'bandaged', npc: 'ceda', x: hob?.x ?? 0, y: hob?.y ?? 0 });
    cue('bandaged');
  }
};

/* ---------- act 3: the sap of the crown ---------- */

/** The low crown's buds or the high one's: which crown a knot is in. */
const knotStage = (r: Sap, k: number): number => (r.nodes[(r.knots[k] as { node: number }).node] as { stage: number }).stage;

/** A pulse up the trunk: each bud of the crown at work opens, stays shut or cracks; three open and it runs. */
const pulse = (s: BattleState, r: Sap, w: St, cue: (c: string) => void): void => {
  const root = r.roots[w.stage] as number;
  const flow = sapFlow(r.nodes, r.knots, root, w.closed.map((x) => x === 1));
  let open = 0;
  w.pulses++;
  ev(s, { t: 'senda', kind: 'pulse', npc: '', x: r.foot.x, y: r.foot.y, n: w.stage });
  for (let i = 0; i < r.nodes.length; i++) {
    const n = r.nodes[i] as { need: number; stage: number; x: number; y: number };
    if (!n.need || n.stage !== w.stage) continue;
    const f = flow[i] as number;
    const was = w.buds[i] as number;
    if ((w.crackUntil[i] as number) > s.tick) {
      // Cracked: shut until it closes, and more on it keeps it open longer.
      if (f === 1) w.crackUntil[i] = s.tick + r.crack;
      w.buds[i] = 1;
      continue;
    }
    if (f === 1) {
      w.buds[i] = 1;
      w.crackUntil[i] = s.tick + r.crack;
      w.cracks++;
      ev(s, { t: 'senda', kind: 'budCrack', npc: '', x: n.x, y: n.y, n: i });
      cue(`crack${w.cracks}`);
      continue;
    }
    w.buds[i] = f;
    if (f === 0) {
      open++;
      if (was !== 0) ev(s, { t: 'senda', kind: 'budOpen', npc: '', x: n.x, y: n.y, n: i });
    }
  }
  w.streak = open >= r.need ? w.streak + 1 : 0;
  if (open >= r.need && w.streak < r.hold) cue(`hold${w.stage}`);
  if (w.streak >= r.hold) {
    w.streak = 0;
    ev(s, { t: 'senda', kind: 'crownRun', npc: '', x: (r.nodes[root] as Pt).x, y: (r.nodes[root] as Pt).y, n: w.stage });
    cue(`run${w.stage}`);
    if (w.stage + 1 < r.roots.length) {
      w.stage++;
      w.ran = s.tick;
      // The frost lets go of what it went for in the low crown.
      w.bknot = -1;
      w.bwarn = -1;
      w.way = [];
    } else w.done = s.tick;
  }
};

/** The hero at a knot, still: its Raíz turns it, and the frozen wood bites back. */
const knotWork = (s: BattleState, r: Sap, w: St, h: Hero | undefined, cue: (c: string) => void): void => {
  if (!h || w.job < 0) return;
  const k = r.knots[w.job];
  if (!k || knotStage(r, w.job) !== w.stage) {
    w.job = -1;
    return;
  }
  if (h.moving || !withinRange(h.x, h.y, k.x, k.y, r.reach)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return;
  }
  if (++w.held < r.cut) return;
  const i = w.job;
  w.closed[i] = w.closed[i] ? 0 : 1;
  w.job = -1;
  w.held = 0;
  w.turns++;
  ev(s, { t: 'senda', kind: 'knot', npc: w.closed[i] ? 'shut' : 'open', x: k.x, y: k.y, n: i });
  damageHero(s, h, mulBp(h.stats.maxHp, r.chillBp), null, 'frost', true);
  ev(s, { t: 'senda', kind: 'chill', npc: '', x: h.x, y: h.y });
  cue(`chill${w.turns}`);
  // The frost lets go of a knot that is open now.
  if (w.bknot === i && !w.closed[i]) {
    w.bknot = -1;
    w.bwarn = -1;
  }
};

/** The graph of the crown: the trunk's foot (−1) to the low crown's fork, to the high crown's fork; each node to its parent. */
const neighbours = (r: Sap, i: number): number[] => {
  const out: number[] = [];
  const n = i >= 0 ? (r.nodes[i] as { parent: number; kids: number[] }) : null;
  if (i < 0) out.push(r.roots[0] as number);
  else {
    if (n && n.parent >= 0) out.push(n.parent);
    for (const c of n?.kids ?? []) out.push(c);
    if (i === r.roots[0]) out.push(-1, r.roots[1] as number);
    if (i === r.roots[1]) out.push(r.roots[0] as number);
  }
  return out;
};
const nodePt = (r: Sap, i: number): Pt => (i < 0 ? r.foot : (r.nodes[i] as Pt));

/** The way along the crown from the node nearest a point to a knot (its points), by a search over a dozen nodes. */
const wayTo = (r: Sap, x: number, y: number, k: number): number[][] => {
  let from = -1;
  let bd = (x - r.foot.x) * (x - r.foot.x) + (y - r.foot.y) * (y - r.foot.y);
  r.nodes.forEach((n, i) => {
    const d = (x - n.x) * (x - n.x) + (y - n.y) * (y - n.y);
    if (d < bd) {
      bd = d;
      from = i;
    }
  });
  const knot = r.knots[k] as { node: number; x: number; y: number };
  const goal = (r.nodes[knot.node] as { parent: number }).parent;
  const prev = new Map<number, number>([[from, from]]);
  const queue = [from];
  while (queue.length) {
    const c = queue.shift() as number;
    if (c === goal) break;
    for (const nb of neighbours(r, c)) {
      if (prev.has(nb)) continue;
      prev.set(nb, c);
      queue.push(nb);
    }
  }
  const path: number[] = [];
  for (let c = goal; prev.has(c) && c !== from; c = prev.get(c) as number) path.push(c);
  path.reverse();
  return [...path.map((i) => [nodePt(r, i).x, nodePt(r, i).y]), [knot.x, knot.y]];
};

const sapTick = (s: BattleState, r: Sap, w: St, cue: (c: string) => void): void => {
  const h = heroOf(s);
  if (w.done >= 0) return;
  if (!w.closed.length) {
    w.closed = r.knots.map(() => 0);
    w.buds = r.nodes.map((n) => (n.need ? -1 : -2));
    w.crackUntil = r.nodes.map(() => 0);
    w.pulseNext = r.pulse;
  }
  knotWork(s, r, w, h, cue);
  if (s.tick >= w.pulseNext) {
    w.pulseNext = s.tick + r.pulse;
    pulse(s, r, w, cue);
  }
  // Its claw landing.
  if (w.claw >= 0 && s.tick >= w.claw) {
    w.claw = -1;
    const boss = w.boss ? findEnemy(s, w.boss) : null;
    ev(s, { t: 'senda', kind: 'claw', npc: '', x: w.cx, y: w.cy });
    for (const x of s.heroes) if (x.downTicks === 0 && withinRange(x.x, x.y, w.cx, w.cy, r.boss.claw.r)) damageHero(s, x, mulBp(x.stats.maxHp, r.boss.claw.bp), boss, 'frost', true);
  }
  // Its frost on a knot: struck off, or the knot opens.
  if (w.bwarn >= 0) {
    const boss = findEnemy(s, w.boss);
    if (!boss || boss.hp <= 0) {
      w.bwarn = -1;
      w.bknot = -1;
    } else if (w.bhp - boss.hp >= mulBp(boss.maxHp, r.boss.scareBp)) {
      ev(s, { t: 'senda', kind: 'frostOff', npc: '', x: boss.x, y: boss.y, n: w.bknot });
      cue('frostOff');
      w.bwarn = -1;
      w.bknot = -1;
      w.way = [];
      w.brest = s.tick + r.boss.rest;
      boss.stun = imax(boss.stun, 24);
    } else if (s.tick >= w.bwarn) {
      const k = w.bknot;
      w.bwarn = -1;
      w.bknot = -1;
      w.way = [];
      w.brest = s.tick + r.boss.rest;
      if (k >= 0 && w.closed[k]) {
        w.closed[k] = 0;
        w.opened++;
        const p = r.knots[k] as Pt;
        ev(s, { t: 'senda', kind: 'frostOpen', npc: '', x: p.x, y: p.y, n: k });
        cue(`opened${w.opened}`);
      }
    }
  }
};

/** The frost that climbs: up the crown to a closed knot (frost on it, 3 s); its claw at the hero now and then. */
const frost = (s: BattleState, r: Sap, w: St, e: Enemy, cue: (c: string) => void): boolean => {
  const h = heroOf(s);
  // (The base counts a stun down only for what it moves itself.)
  if (e.stun > 0) {
    e.stun--;
    e.anim = 'stun';
    return true;
  }
  if (w.done >= 0) {
    e.anim = 'idle';
    return true;
  }
  // The claw: the hero's spot marked 3 s.
  if (h && s.waveT >= r.boss.claw.first && s.tick >= w.clawNext && w.claw < 0) {
    w.clawNext = s.tick + r.boss.claw.every;
    w.claw = s.tick + r.boss.claw.warn;
    w.cx = h.x;
    w.cy = h.y;
    w.claws++;
    ev(s, { t: 'senda', kind: 'clawWarn', npc: '', x: h.x, y: h.y, n: r.boss.claw.warn });
    cue(`claw${w.claws}`);
  }
  if (w.bwarn >= 0) {
    e.anim = 'attack';
    return true;
  }
  // A closed knot of the crown at work, the nearest to it.
  if (!w.way.length && s.tick >= w.brest) {
    let best = -1;
    let bd = 0x7fffffff;
    for (let k = 0; k < r.knots.length; k++) {
      if (!w.closed[k] || knotStage(r, k) !== w.stage) continue;
      const p = r.knots[k] as Pt;
      const d = idiv((p.x - e.x) * (p.x - e.x) + (p.y - e.y) * (p.y - e.y), 1000);
      if (d < bd) {
        bd = d;
        best = k;
      }
    }
    if (best >= 0) {
      w.bknot = best;
      w.way = wayTo(r, e.x, e.y, best);
      if (w.opened === 0 && w.claws < 2) cue('frost1');
    }
  }
  if (w.bknot >= 0 && !w.closed[w.bknot]) {
    // Opened meanwhile (by the hero): another.
    w.bknot = -1;
    w.way = [];
  }
  const to = w.way[0];
  if (!to) {
    e.anim = 'idle';
    return true;
  }
  e.anim = 'walk';
  stepToward(e, to[0] as number, to[1] as number, r.boss.pace);
  if (withinRange(e.x, e.y, to[0] as number, to[1] as number, 80)) {
    w.way.shift();
    if (!w.way.length && w.bknot >= 0) {
      w.bwarn = s.tick + r.boss.warn;
      w.bhp = e.hp;
      ev(s, { t: 'senda', kind: 'frostWarn', npc: '', x: e.x, y: e.y, n: w.bknot });
    }
  }
  return true;
};

/* ---------- the act ---------- */

export const winterTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.winter;
  const sr = s.senda;
  if (!r || !sr) return;
  if (!sr.winter) {
    const w = fresh();
    sr.winter = w;
    if (r.sap) {
      const e = spawnEnemy(s, 'rime', 1, false, 0, r.sap.foot.x, r.sap.foot.y + 400, r.bossHp);
      w.boss = e.id;
      s.bossId = e.id;
      w.brest = r.sap.boss.first;
      w.clawNext = r.sap.boss.claw.first;
    }
  }
  const w = sr.winter;
  if (!s.started) return;
  if (w.pend.length) {
    for (const c of w.pend) cue(c);
    w.pend.length = 0;
  }
  if (r.boughs) boughsTick(s, r.boughs, w, cue);
  else if (r.hollow) hollowTick(s, r.hollow, w, cue);
  else if (r.sap) sapTick(s, r.sap, w, cue);
  serpTick(s, r, w, cue);
  w.t++;
};

/** A serpent under the wood (or swelling it): nothing reaches it (combat's `isRevealed`). */
export const winterHidden = (s: BattleState, e: Enemy): boolean => {
  const m = s.senda?.winter?.sp[e.id];
  return !!m && (m[0] === 0 || m[0] === 1);
};

/**
 * What lands no blow on the path's people by standing at them (battle/senda.ts `struck`): a serpent (its bite is
 * counted here), the frost that climbs (its claw is the hero's), a crow (they hunt the hero).
 */
export const winterSpares = (s: BattleState, e: Enemy): boolean => {
  const w = s.senda?.winter;
  if (!w) return false;
  return !!w.sp[e.id] || e.id === w.boss || e.kind === 'crow';
};

/** Moved by the act's own rule (true): the serpents (still where they come out), the frost that climbs. */
export const winterHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = s.rules?.senda?.winter;
  const w = s.senda?.winter;
  if (!r || !w) return false;
  void px;
  const cue = (c: string): void => {
    w.pend.push(c);
  };
  const m = w.sp[e.id];
  if (m) {
    if (e.stun > 0) e.stun--;
    e.anim = m[0] === 2 && e.stun <= 0 ? 'attack' : 'idle';
    return true;
  }
  if (r.sap && e.id === w.boss) return frost(s, r.sap, w, e, cue);
  return false;
};

/** The hero's pace on the boughs: in a heap of snow it wades. */
export const winterSpeedBp = (s: BattleState, h: Hero): number => {
  const b = s.rules?.senda?.winter?.boughs;
  const w = s.senda?.winter;
  if (!b || !w) return 10000;
  for (const p of w.heaps) if (withinRange(h.x, h.y, p[0] as number, p[1] as number, b.snow.heapR)) return 10000 - b.snow.heapBp;
  return 10000;
};

/** A tap on a knot (the test): the hero goes to stand by it, and its Raíz turns it. */
export const winterOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.winter?.sap;
  const w = s.senda?.winter;
  if (!r || !w || w.done >= 0 || at < 0) return;
  const k = r.knots[at];
  if (!k || knotStage(r, at) !== w.stage) return;
  w.job = at;
  w.held = 0;
  h.target = 0;
  h.forced = false;
  orderMove(s, h, k.x, k.y);
};

/** Another order of the hero's own: the knot it was sent to is off. */
export const winterMarkOff = (s: BattleState): void => {
  const w = s.senda?.winter;
  if (w) w.job = -1;
};

/** How far the goal has come: up the way, the bandage, the crowns run (and the buds open in the one at work). */
export const winterGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.winter;
  const w = s.senda?.winter;
  if (!r || !w) return { done: 0, need: 1 };
  if (r.boughs) {
    if (w.done >= 0) return { done: 1, need: 1 };
    const h = heroOf(s);
    const a = r.boughs.way[0] as Pt;
    const total = imax(1, a.y - r.boughs.top.y);
    return { done: h ? imax(0, imin(total, a.y - h.y)) : 0, need: total };
  }
  if (r.hollow) return { done: w.done >= 0 ? r.hollow.need : w.work, need: r.hollow.need };
  const sp = r.sap;
  if (!sp) return { done: 0, need: 1 };
  return { done: w.done >= 0 ? sp.roots.length : w.stage, need: sp.roots.length };
};

/** Won a moment after the goal; lost with Hob or Ceda down where they are vital (the path's base counts that). */
export const winterResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.winter;
  const w = s.senda?.winter;
  if (!r || !w) return null;
  if (w.lost) return 'lost';
  if (w.done >= 0) return s.tick >= w.done + (r.sap ? 36 : 18) ? 'won' : null;
  return null;
};

/** The knot nearest a point within `d` (the HUD's offer and the bot), −1 if none in the crown at work. */
export const knotNear = (s: BattleState, x: number, y: number, d: number): number => {
  const r = s.rules?.senda?.winter?.sap;
  const w = s.senda?.winter;
  if (!r || !w) return -1;
  let best = -1;
  let bd = d * d;
  r.knots.forEach((k, i) => {
    if (knotStage(r, i) !== w.stage) return;
    const dd = (k.x - x) * (k.x - x) + (k.y - y) * (k.y - y);
    if (dd < bd) {
      bd = dd;
      best = i;
    }
  });
  return best;
};
